'use client';

import type { SeriesWithSlideDeck as Series } from '@mentimeter/presentation-schema/api-types-overrides';
import { usePresentationResults } from '@mentimeter/presentation-data-hooks';
import { usePresentationSlides } from '@mentimeter/presentation-data-hooks/slide';
import type { PresentationMode } from '@mentimeter/presentation-url-utils';
import {
  PresentationProvider,
  PaceProvider as PublicPaceProvider,
  QfaProvider,
  QuestionResultsProvider,
  ReactionsResultsProvider,
  SfinxPresentationSettingsProvider,
  ThemeProvider,
} from '@mentimeter/sfinx';
import { NextIntlPresentingProvider } from '@mentimeter/i18n-presenting';
import { useQfa } from 'features/core/presentation/public/useQfa';
import { useSyncPresentStateAndURL } from 'features/core/presentation/shared/pace/useSyncPresentStateAndURL';
import { QuestionProvider } from 'features/core/presentation/shared/question';
import { QuizResultProvider } from 'features/core/presentation/shared/quiz-module/QuizResultProvider';
import SegmentationProvider from 'features/core/presentation/shared/segmentation';
import * as React from 'react';
import { useReactionsResults } from 'features/core/editor/state/reactions-results/useReactionsResults';
import { usePublicPresentationState } from './usePublicPresentationState';

interface Props {
  series: Series;
  questionId: string;
  mode: PresentationMode;
  children: React.ReactNode;
}

export const PresentationContainer = ({
  series,
  questionId,
  mode,
  children,
}: Props) => {
  const { segmentations, id: seriesId, theme, language } = series;
  const { slides } = usePresentationSlides(seriesId);

  const slidePublicKeyToInitWith = slides.find(
    (q) => q.id === questionId,
  )?.slidePublicKey;

  const presentationStore = usePublicPresentationState({
    slidePublicKey: slidePublicKeyToInitWith,
    series,
  });

  const activeQuestion = slides.find((q) => {
    return q.slidePublicKey === presentationStore?.state?.slidePublicKey;
  });

  useSyncPresentStateAndURL({
    mode,
    questions: slides,
    setActiveSlide: presentationStore.actions.setActiveSlide,
  });

  const { result, isPlaceholderResults } = usePresentationResults({
    question: activeQuestion,
    sessionId: 'current',
    slideDeckPublicKey: seriesId,
  });
  const questionResults = React.useMemo(
    () => (result ? { ...result, isPlaceholderResults } : undefined),
    [result, isPlaceholderResults],
  );
  const reactionsResults = useReactionsResults(activeQuestion?.id);

  const qfa = useQfa();

  if (!questionId || !presentationStore.state || !activeQuestion) {
    return null;
  }

  return (
    <PublicPaceProvider value={presentationStore}>
      <QuestionProvider activeQuestion={activeQuestion}>
        <SegmentationProvider
          questions={slides}
          activeQuestionId={activeQuestion.id}
          segmentations={segmentations}
        >
          <SfinxPresentationSettingsProvider
            series={series}
            qaModerationPolicy="not-moderated"
            participationIdentityMode="anonymous"
            qaQuestionVisibility="moderators-only"
            liveChatMode="disabled"
          >
            <PresentationProvider
              value={{
                showTrends: false,
              }}
            >
              <QuestionResultsProvider value={questionResults}>
                <ReactionsResultsProvider value={reactionsResults.data}>
                  <QuizResultProvider
                    type={activeQuestion.type}
                    questionId={activeQuestion.id}
                    isTestVotesEnabled={false}
                  >
                    <NextIntlPresentingProvider language={language}>
                      <QfaProvider value={qfa}>
                        <ThemeProvider
                          presentationTheme={theme}
                          slideTheme={
                            activeQuestion.override_theme
                              ? activeQuestion.theme_settings
                              : undefined
                          }
                        >
                          {children}
                        </ThemeProvider>
                      </QfaProvider>
                    </NextIntlPresentingProvider>
                  </QuizResultProvider>
                </ReactionsResultsProvider>
              </QuestionResultsProvider>
            </PresentationProvider>
          </SfinxPresentationSettingsProvider>
        </SegmentationProvider>
      </QuestionProvider>
    </PublicPaceProvider>
  );
};
